import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Dropdown } from "react-bootstrap";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import { useTranslation } from "react-i18next";
import styled, { css, withTheme } from "styled-components";
import { decimalRegex, FoodType } from "../../../modules/constants";
import DefaultTable, { Line } from "../../ui-library/CustomTable/DefaultTable";
import OutlineExpandTitleButton from "../../ui-library/Buttons/OutlineExpandTitleButton/OutlineExpandTitleButton";
import LightExpandTitleButton from "../../ui-library/Buttons/LightButton/LightExpandTitleButton";
import { TableDropDownMenu } from "../../ui-library/TableDropDown/TableDropDown";
import { isRtl } from "../../../i18n/helpers";

const StyledDiv = styled("div")`
  color: ${(props) => props?.theme?.black};

  width: 100%;
  margin: auto;

  .hide {
    display: none;
  }

  h6 {
    font-weight: 600;
  }

  th {
    font-weight: 600;
  }

  th:first-child,
  td:first-child {
    width: 66%;
    padding-left: 0px;
  }

  td {
    padding-top: 0.25em;
    padding-bottom: 0.25em;
  }

  .meal-food-search-wrapper {
    width: 100%;

    #food-drink-search {
      left: -2em !important;

      transform: ${(props) =>
        props.isRtl
          ? `translate3d(-30px, 38px, 0px) !important`
          : `translate3d(30px, 38px, 0px) !important`};
    }
  }

  .meal-food-search-wrapper .form-control {
    border-bottom: 2px solid ${(props) => props?.theme?.pastelLike};

    &:hover,
    &:active,
    &:focus {
      border-bottom: 2px solid #a7208c;
    }
  }

  .meal-food-search-wrapper .form-control input {
    font-size: 15px;
    font-weight: 600;
  }

  .rbt-token-removeable {
    display: none;
  }
`;

const ItemColumn = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledRemoveIcon = styled("i")`
  color: ${(props) => props?.theme?.mortar};
  font-size: 18px;
  margin-right: 9px;
  cursor: pointer;
`;

const StyledNoEntries = styled("div")`
  text-align: center;
  color: rgba(0, 0, 0, 0.22);
`;

const StyledInputQty = styled("input")`
  border-bottom: 2px solid ${(props) => props?.theme?.pastelLike};
  text-align: center;
  min-width: 45px;

  &.form-control {
    font-weight: 600;
    font-size: 12px;
  }
  &.new-answer {
    color: ${(props) => props?.theme?.mortar};
    background-color: transparent;

    &:hover,
    &:active,
    &:focus {
      background-color: transparent;
    }
  }
`;

const StyledExpandTitleButton = styled(LightExpandTitleButton)`
  overflow: hidden;

  ${(props) =>
    props.hasshadow === "1" &&
    css`
      box-shadow: 0px 3px 6px #0000001a !important;
    `};
`;

const StyledDropDwonToggle = styled(Dropdown.Toggle)`
  background-color: ${(props) => props?.theme?.whiteSmoke};
  border-color: ${(props) => props?.theme?.whiteSmoke};
  color: ${(props) => props?.theme?.mortar};
  padding: 0.5rem 0.7rem 0.5rem 0.4rem;
  font-weight: 600;
  font-size: 0.75rem;
  width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;

  ${(props) =>
    props.hasshadow === "1" &&
    css`
      box-shadow: 0px 3px 6px #0000001a;
    `};

  &:hover,
  &:active,
  &:focus,
  &.focus {
    background-color: ${(props) => props?.theme?.whiteSmoke};
    border-color: ${(props) => props?.theme?.whiteSmoke};
    color: ${(props) => props?.theme?.mortar};
    box-shadow: 0px 3px 6px #0000001a;
  }

  &::after {
    font-family: "Font Awesome 5 Free";
    font-weight: 900;
    content: "\f107";
    border: 0px;
    vertical-align: initial;
    position: absolute;
    right: 5px;
  }
`;

const Title = styled.span`
  color: ${(props) => props.theme?.blackLite};
  font-weight: 600;
  font-size: 18px;
`;

const TitleDivider = styled.div`
  width: 100%;
  height: 2px;
  background-color: ${(props) => props.theme?.blackLite};
`;

const UnitDropdown = styled(Dropdown)``;

const MCTIndicator = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  margin-right: -2px;
  margin-top: -2px;
  border-top-right-radius: 6.4px;
  border-bottom-right-radius: 6.4px;
  height: 53px;
  width: 7px;
  background-color: ${(props) => props.theme?.blueMagenta};
`;

const Container = styled.div`
  ${(props) =>
    props.hasBackground &&
    css`
      padding: 12px;
      background-color: ${(props) => props.theme?.whiteSmoke};
    `};
`;

const FoodDrinkamountDropDown = (props) => {
  const {
    selectedItems,
    setSelectedItems,
    selectedItem,
    lineNumber,
    placeInElementRef,
    readOnly,
    hasBackground,
  } = props;

  const { t } = useTranslation();

  const [dropTitle, setDropTitle] = useState(null);

  useEffect(() => {
    setDropTitle(
      selectedItem?.foodType === FoodType.CUSTOM
        ? t(selectedItem.selectedMeasure.measure)
        : selectedItem.selectedMeasure.measure
    );
  }, [selectedItem, t]);

  const handleDropDownChange = (e, measure) => {
    setDropTitle(e.target.textContent);

    const newItem = { ...selectedItem, selectedMeasure: measure };

    setSelectedItems(
      selectedItems.map((item, index) => {
        return index === lineNumber ? newItem : item;
      })
    );
  };

  return (
    <UnitDropdown
      drop="down"
      data-testid="measures-dropdown"
      align={isRtl() ? "end" : "start"}
    >
      <StyledDropDwonToggle
        variant="fodd-drink"
        hasshadow={hasBackground ? "1" : "0"}
      >
        {dropTitle}
      </StyledDropDwonToggle>
      <TableDropDownMenu placeInElement={placeInElementRef?.current}>
        <Dropdown.Item
          data-testid="default-measure-item"
          onClick={(e) => {
            if (!readOnly) {
              handleDropDownChange(e, selectedItem.selectedMeasure);
            }
          }}
        >
          {selectedItem.foodType === FoodType.CUSTOM
            ? t(selectedItem.selectedMeasure.measure)
            : selectedItem.selectedMeasure.measure}
        </Dropdown.Item>
        {!readOnly &&
          selectedItem?.measures?.map((item, i) => {
            return item.measure !== selectedItem.selectedMeasure.measure ? (
              <Dropdown.Item
                data-testid={`drop_item_${lineNumber}_${i}`}
                key={`drop_item_${lineNumber}_${i}`}
                onClick={(e) => {
                  handleDropDownChange(e, item);
                }}
              >
                {selectedItem.foodType === FoodType.CUSTOM
                  ? t(item.measure)
                  : item.measure}
              </Dropdown.Item>
            ) : null;
          })}
      </TableDropDownMenu>
    </UnitDropdown>
  );
};

const FoodDrinkQtyInput = (props) => {
  const {
    selectedItems,
    setSelectedItems,
    lineNumber,
    selectedItem,
    readOnly,
  } = props;
  const [qty, setQty] = useState("");

  useEffect(() => {
    const value = selectedItem.qtyText ?? selectedItem.qty?.toString() ?? "";
    setQty(value);
  }, [selectedItem]);

  const handleQtyChange = (e) => {
    const newValue = e.target.value.replace(" ", "").replace(",", ".");

    if (newValue === "" || decimalRegex.test(newValue)) {
      setQty(newValue);

      const newValueParsed = parseFloat(newValue);

      const newItem = {
        ...selectedItem,
        qty: isNaN(newValueParsed) ? 0 : newValueParsed,
        qtyText: newValue,
      };

      setSelectedItems(
        selectedItems.map((item, index) => {
          return index === lineNumber ? newItem : item;
        })
      );
    }
  };

  return (
    <StyledInputQty
      data-testid="foodDrinkQtyInput"
      type="text"
      className="form-control new-answer"
      placeholder=""
      aria-label="Qty"
      aria-describedby="Qty"
      onChange={handleQtyChange}
      value={qty}
      readOnly={readOnly}
      maxLength="4"
    />
  );
};

const FoodDrinkSelector = (props) => {
  const {
    theme,
    inputTitle,
    inputPlaceholder,
    handleSearch,
    handleSelect,
    totalitems,
    selectedItems,
    setSelectedItems,
    children,
    defaultItems,
    readOnly,
    title = "My items",
    hasBackground,
    showData,
    ...styledDivProps
  } = props;

  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [options, setOptions] = useState([]);

  const [defaultItemAdded, setDefaultItemAdded] = useState(false);

  const handleFoodChange = (selectedOptions) => {
    setIsAdding(true);

    const tmpSelected = selectedOptions[selectedOptions.length - 1];

    onSelectItem(tmpSelected);
    setOptions([]);
  };

  const processSelectItem = useCallback((data) => {
    data.qty = 1;
    data.selectedMeasure = data?.defaultMeasure;
    return data;
  }, []);

  const onSelectItem = useCallback(
    (selected, asynchCall = true) => {
      if (asynchCall) {
        handleSelect(selected.item, selected.type, selected.locale)
          .then((data) => {
            if (!data) return null;
            data = processSelectItem(data);
            const tmpOptions = [data, ...selectedItems];
            setSelectedItems(tmpOptions);
            setIsAdding(false);
          })
          .finally(() => {
            setIsAdding(false);
          });
      } else {
        return handleSelect(selected.item, selected.type, selected.locale);
      }
    },
    [handleSelect, selectedItems, setSelectedItems, processSelectItem]
  );

  const searchData = (query) => {
    setIsLoading(true);

    handleSearch(query)
      .then((data) => {
        setOptions(data);
        setIsLoading(false);
      })
      .finally(() => {
        setIsAdding(false);
      });
  };

  useEffect(() => {
    if (defaultItems && !defaultItemAdded) {
      /*
       * When there is more than one default item we first build / obtain
       * the API information and then set the reducer information
       * in order to only make 1 reducer call instead of muliple ones.
       */
      const processAsyncDefaultItem = async () => {
        setDefaultItemAdded(true);

        let tmpData = [];
        for (let index = 0; index < defaultItems.length; index++) {
          const item = defaultItems[index];
          try {
            let data = await onSelectItem({
              title: item?.title,
              item: item?.item,
              type: item?.foodType,
            });
            data = processSelectItem(data);
            tmpData.push(data);
          } catch (error) {}
        }

        setSelectedItems(tmpData);
      };

      processAsyncDefaultItem();
    }
  }, [
    defaultItemAdded,
    defaultItems,
    onSelectItem,
    selectedItems,
    setSelectedItems,
    processSelectItem,
  ]);

  // Bypass client-side filtering by returning `true`. Results are already
  // filtered by the search endpoint, so no need to do it again.
  const filterBy = () => true;

  const defaultProps = {
    headers: [
      { label: t("appText.item") },
      { label: t("appText.qty") },
      { label: t("appText.unit") },
    ],
    maincolor: theme?.darkPurple,
    totalitems: totalitems,
    hasSearch: false,
    haspagesizefilter: false,
    haspagination: false,
    searchedParams: {
      search: "",
    },
    noLineBorder: true,
    topControlsClass: "hide",
    responsivetable: 0,
    showNoDataLabel: false,
    lineHeaderEnabled: false,
  };

  const placeInElementRef = useRef(null);

  const getChangeToItem = (item) => {
    if (item.removed) {
      return (
        <OutlineExpandTitleButton size="sm" type="danger" cutAtChar="18">
          {item.title?.toUpperCase()}
        </OutlineExpandTitleButton>
      );
    }

    if (item.addedOrModified) {
      return (
        <OutlineExpandTitleButton size="sm" type="accent" cutAtChar="18">
          {item.title?.toUpperCase()}
        </OutlineExpandTitleButton>
      );
    }

    return (
      <StyledExpandTitleButton
        size="sm"
        cutAtChar="18"
        hasshadow={hasBackground ? "1" : "0"}
      >
        {item.title?.toUpperCase()}

        {item.foodType === FoodType.CUSTOM && <MCTIndicator />}
      </StyledExpandTitleButton>
    );
  };

  return (
    <StyledDiv
      data-testid="foodDrinkSelector"
      className="food-drink-selector"
      ref={placeInElementRef}
      {...styledDivProps}
      isRtl={isRtl()}
    >
      {readOnly ? (
        <>
          <Title>{title}</Title>
          <TitleDivider />
        </>
      ) : (
        <>
          <h6>{inputTitle}</h6>
          <div className="input-group mb-1">
            <AsyncTypeahead
              filterBy={filterBy}
              id="food-drink-search"
              labelKey="title"
              isLoading={isLoading || isAdding}
              minLength={1}
              onSearch={searchData}
              options={options}
              multiple
              autoFocus
              emptyLabel={t("appText.search.emptyLabel")}
              searchText={t("appText.search.searchText")}
              promptText={t("appText.search.promptText")}
              onInputChange={(text) => {
                if (text.length === 0) {
                  setOptions([]);
                }
              }}
              delay={500}
              useCache={false}
              placeholder={
                inputPlaceholder ||
                t("appText.dietDiary.addMeal.search.placeholder")
              }
              onChange={handleFoodChange}
              selected={selectedItems}
              renderMenuItemChildren={(option, props) => (
                <div>
                  <span>{option.title}</span>
                </div>
              )}
              className="meal-food-search-wrapper"
              inputStyle=""
              inputProps={{
                "data-testid": "inputSearchComponent",
                name: "inputSearchComponent",
              }}
            />
          </div>
        </>
      )}

      <Container className="pb-3 pt-2" hasBackground={hasBackground}>
        <DefaultTable {...{ ...defaultProps }}>
          {showData &&
            selectedItems?.map((selectedItem, index) => {
              return (
                <Line key={`table_line_${index}`}>
                  <ItemColumn>
                    {!readOnly && (
                      <StyledRemoveIcon
                        onClick={() => {
                          const tmpMulti = [...selectedItems];
                          tmpMulti.splice(index, 1);
                          setSelectedItems([...tmpMulti]);
                        }}
                        className="fas fa-trash"
                        aria-hidden="true"
                        data-testid="remove-button"
                      />
                    )}

                    {getChangeToItem(selectedItem)}
                  </ItemColumn>
                  <span>
                    <FoodDrinkQtyInput
                      quantity={selectedItem.qty}
                      selectedItem={selectedItem}
                      index={index}
                      selectedItems={selectedItems}
                      setSelectedItems={setSelectedItems}
                      lineNumber={index}
                      readOnly={readOnly}
                    />
                  </span>
                  <span>
                    <FoodDrinkamountDropDown
                      selectedItem={selectedItem}
                      lineNumber={index}
                      selectedItems={selectedItems}
                      setSelectedItems={setSelectedItems}
                      placeInElementRef={placeInElementRef}
                      readOnly={readOnly}
                      hasBackground={hasBackground}
                    />
                  </span>
                </Line>
              );
            })}
        </DefaultTable>
        {selectedItems?.length > 0 || !showData ? null : (
          <StyledNoEntries>
            {t("appText.dietDiary.addMeal.noEntries")}
          </StyledNoEntries>
        )}
      </Container>

      {children}
    </StyledDiv>
  );
};

FoodDrinkSelector.propTypes = {
  inputTitle: PropTypes.string,
  inputPlaceholder: PropTypes.string,
  totalitems: PropTypes.number,
  readOnly: PropTypes.bool,
};

FoodDrinkSelector.defaultProps = {
  inputTitle: "What did you eat?",
  totalitems: 0,
  readOnly: false,
};

export default withTheme(FoodDrinkSelector);
